import { injectable } from 'inversify';
import { createSelector } from '@reduxjs/toolkit';
import { useAppSelector } from '../../state/hooks';
import { AuthService } from '../../state/helperServices/auth/auth.service';
import {
  IncidentRepository,
  PlayerRepository,
  TeamRepository,
} from '../../state/reduxRepositories';
import { PlayerProfilePageQueryService } from './playerProfilePage.queryParam.service';
import { Player } from '../../state/reduxRepositories/players/player.interfaces';
import { Team } from '../../state/reduxRepositories/teams/team.interfaces';
import {
  LoginUserAccountType,
  SelectedPlayer,
} from './components/playerProfile.interface';
import { LoggedInUser } from '../../state/helperServices/auth/auth.interfaces';
import { PlayerIncidentList } from '../incidentsList/incidentsList.interface';
import { Incident } from '../../state/reduxRepositories/incident/incident.interface';
import { IncidentDomain } from '../../domains/incident.domain';

@injectable()
export class PlayerProfileDomain {
  openIncidentSelector: any;
  openIncidentByPlayerSelector: any;
  currentPlayerSelector: any;
  currentUserSelector: any;

  public constructor(
    private incidentRepository: IncidentRepository,
    private authService: AuthService,
    private playerRepository: PlayerRepository,
    private playerProfileQueryService: PlayerProfilePageQueryService,
    private teamRepository: TeamRepository,
    private incidentDomain: IncidentDomain,
  ) {
    this.createCurrentPlayerSelector();
    this.createCurrentUserSelector();
    this.createOpenIncidentByPlayerSelector();
  }

  useUserSelector = (): LoginUserAccountType => {
    return useAppSelector(this.currentUserSelector);
  };

  useCurrentPlayerSelector = (): SelectedPlayer => {
    return useAppSelector(this.currentPlayerSelector);
  };

  useOpenIncidentByPlayerSelector = (): PlayerIncidentList => {
    return useAppSelector(this.openIncidentByPlayerSelector);
  };

  createOpenIncidentByPlayerSelector = () => {
    this.openIncidentByPlayerSelector = createSelector(
      this.playerProfileQueryService.mergedQuerySelector,
      this.incidentDomain.openIncidentSelector,
      (queryParams, playerIncidentsList: PlayerIncidentList[]) => {
        if (!queryParams?.selectedPlayerId) {
          return playerIncidentsList;
        }

        const incidents = playerIncidentsList.filter(
          (list) => queryParams?.selectedPlayerId === list?.playerId,
        );

        if (!incidents.length) {
          return undefined;
        }
        return incidents[0];
      },
    );
  };

  // TODO: find a way not to duplicate this code with incidentListDomainService
  private getIncidentMaximumDatePerPlayer = (
    incidents: any,
    playerId?: string,
  ) => {
    const filter = incidents.filter(
      (incident: Incident) => incident.playerId === playerId,
    );
    const maxDate = Math.max(...filter.map((e: any) => new Date(e.createdAt)));
    return new Date(maxDate).getTime();
  };

  /** 
   TODO:
     we might need to find a global place for this selector 
     since we call user on some other pages just to get accountType
   */
  createCurrentUserSelector = () => {
    this.currentUserSelector = createSelector(
      this.authService.loggedInUserSelector,
      (user: LoggedInUser): LoginUserAccountType => {
        return {
          accountType: user?.accountType,
        };
      },
    );
  };

  createCurrentPlayerSelector = () => {
    this.currentPlayerSelector = createSelector(
      this.playerProfileQueryService.playerProfilePageQuerySelector,
      this.playerRepository.selectors.resourcesSelector,
      this.teamRepository.selectors.resourcesSelector,
      (queryParams: any, players: Player[], teams: Team[]): SelectedPlayer => {
        const findPlayer = players.find(
          (player) => player.id === queryParams.selectedPlayerId,
        );

        const findTeam = teams.find((team) =>
          team.playerIds.includes(queryParams.selectedPlayerId),
        );

        return {
          firstName: findPlayer?.firstName,
          lastName: findPlayer?.lastName,
          clearedToPlay: findPlayer?.clearedToPlay,
          id: findPlayer?.id,
          teamName: findTeam?.name,
        };
      },
    );
  };
}
